import { useEffect, useRef, useState } from "react";

import { useData } from "data/data-provider";
import SectionGridAllFleet from "./SectionGridAllFleet";
import { useLocation, useNavigate } from "react-router-dom";
// Reuse Design B's polished FAQ component (rounded chevron-accordion +
// 2-column card grid) so the Design A home FAQ renders the same layout
// variants as Design B instead of the plain RentalPolicies grid (1NOW-1245).
import FAQ from "designs/DesignB/components/FAQ/FAQ";
import SectionTestimonials from "designs/DesignA/components/SectionTestimonials/SectionTestimonials";
import WhyChooseUS from "designs/DesignA/components/WhyChooseUS/WhyChooseUs";
import SectionAbout from "../PageAbout/SectionAbout";
import SectionHero from "designs/DesignA/components/SectionHero/SectionHero";
import GlobalPopUp from "shared/pop-up/global_popUp";

// Wix-style per-section vertical-spacing preset (Section.vertical_spacing,
// chosen in the FMS). Maps to the wrapper's top margin — the gap above the
// section. Range widened so the difference between presets is clearly
// visible; "normal"/unset stays at Design A's original "mt-16" so existing
// client sites are byte-for-byte unchanged until an admin picks a preset.
const sectionSpacing = (preset?: string | null) => {
  switch (preset) {
    case "compact":
      return "mt-6";
    case "spacious":
      return "mt-28";
    default:
      return "mt-16"; // "normal" / unset — current Design A rhythm
  }
};

// A section shows unless the admin explicitly toggled it off in the FMS.
// `is_visible` is only ever false when hidden; null/undefined (older rows)
// read as visible, so existing sites are unaffected.
const isShown = (section: any) => section?.is_visible !== false;

function PageHome() {
  const { aboutUs, whyUs, testimonials, faqs, ourVehicles, popUp } = useData();
  const location = useLocation();
  const navigate = useNavigate();
  const hasScrolled = useRef(false);
  const [showPopUp, setShowPopUp] = useState(false);

  useEffect(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
  }, []);

  useEffect(() => {
    const hash = location.hash;

    if (
      hash &&
      hash.length > 1 &&
      !location.pathname.includes("loading") &&
      !hasScrolled.current
    ) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          setTimeout(() => {
            navigate(location.pathname, { replace: true });
            hasScrolled.current = true;
          }, 1000);
        }
      }, 500);
    }
    if (!hash) {
      hasScrolled.current = false;
    }
  }, [
    location.hash,
    location.pathname,
    navigate,
    aboutUs,
    whyUs,
    testimonials,
    faqs,
    ourVehicles,
  ]);

  useEffect(() => {
    if (popUp && popUp?.heading && popUp?.description) {
      const hasShown = sessionStorage.getItem("hasShownWelcomePopup");
      if (!hasShown) {
        setShowPopUp(true);
        sessionStorage.setItem("hasShownWelcomePopup", "true");
      }
    }
  }, [popUp]);

  const handleClosePopUp = () => {
    setShowPopUp(false);
    sessionStorage.setItem("hasShownWelcomePopup", "true");
  };

  return (
    <>
      {/* Single consistent container per section. Earlier this page nested
          two containers — the outer one forced `padding: 0`, the inner ones
          stripped horizontal padding via `sm:px-0`, but the FAQs section sat
          outside that wrapper with the default 1rem container padding. The
          mismatch caused the left/right alignment to jump between sections.
          Now every section is a sibling `container` so they share the same
          gutter at every breakpoint. */}
      <div className="relative">
        <SectionHero />

        <div className="container relative mt-16" id="section-all-fleet">
          <SectionGridAllFleet />
        </div>

        {isShown(aboutUs) && aboutUs && (
          <div
            className={`container ${sectionSpacing(aboutUs?.vertical_spacing)}`}
            id="section-about-us"
          >
            <SectionAbout aboutUs={aboutUs} />
          </div>
        )}

        {isShown(whyUs) &&
          (whyUs?.heading ||
            whyUs?.description ||
            whyUs?.image_url ||
            whyUs?.keypoints?.length > 0) && (
            <div
              className={`container ${sectionSpacing(whyUs?.vertical_spacing)}`}
              id="section-why-choose-us"
            >
              <WhyChooseUS />
            </div>
          )}

        {isShown(testimonials) &&
          (testimonials?.heading || testimonials?.keypoints?.length > 0) && (
            <div
              className={`container ${sectionSpacing(
                testimonials?.vertical_spacing
              )}`}
              id="section-testimonials"
            >
              <SectionTestimonials uniqueClassName="PageAbout_" />
            </div>
          )}

        {isShown(faqs) &&
          (faqs?.heading ||
            faqs?.description ||
            faqs?.keypoints?.length > 0) && (
            <div
              className={`container ${sectionSpacing(faqs?.vertical_spacing)}`}
              id="section-faqs"
            >
              <FAQ />
            </div>
          )}
        {popUp && popUp?.heading && popUp?.description && (
          <GlobalPopUp
            onClose={() => { handleClosePopUp() }}
            isShowModal={showPopUp}
            popUpData={popUp}
          />
        )}
      </div>
    </>
  );
}

export default PageHome;
